/**
 * Componente principal del calendario de mantenimiento
 */

import { useState, useMemo } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Search,
  Filter,
  CalendarDays,
  List,
} from 'lucide-react';
import { format, isSameDay, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePlotterMaintenanceList,
  usePlotters,
  useChangeMaintenanceStatus,
  useDeleteMaintenance,
} from '@/hooks/usePlotterMaintenance';
import type { PlotterMaintenance, PlotterMaintenanceStatus } from '@/types/plotterMaintenance';
import { TASK_TYPE_CONFIG, STATUS_CONFIG } from '@/types/plotterMaintenance';
import { MaintenanceStats } from './MaintenanceStats';
import { MaintenanceCard } from './MaintenanceCard';
import { MaintenanceFormDialog } from './MaintenanceFormDialog';
import { CompleteMaintenanceDialog } from './CompleteMaintenanceDialog';
import { cn } from '@/lib/utils';

type ViewMode = 'calendar' | 'list';

export function MaintenanceCalendar() {
  const { userRole } = useAuth();
  const canManage = userRole === 'admin' || userRole === 'supervisor';

  // Estado
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [selectedMachineId, setSelectedMachineId] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<PlotterMaintenanceStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<PlotterMaintenance | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completingTask, setCompletingTask] = useState<PlotterMaintenance | null>(null);

  // Data
  const { data: plotters = [] } = usePlotters();
  const { data: tasks = [], isLoading } = usePlotterMaintenanceList({
    machineId: selectedMachineId === 'all' ? undefined : selectedMachineId,
    status: selectedStatus === 'all' ? undefined : selectedStatus,
  });

  const changeStatusMutation = useChangeMaintenanceStatus();
  const deleteMutation = useDeleteMaintenance();

  // Filtrar tareas por búsqueda
  const filteredTasks = useMemo(() => {
    if (!searchTerm) return tasks;
    const term = searchTerm.toLowerCase();
    return tasks.filter(task =>
      task.title.toLowerCase().includes(term) ||
      task.description?.toLowerCase().includes(term)
    );
  }, [tasks, searchTerm]);

  // Tareas del día seleccionado
  const tasksForSelectedDate = useMemo(() => {
    return filteredTasks.filter(task =>
      isSameDay(new Date(task.scheduled_date), selectedDate)
    );
  }, [filteredTasks, selectedDate]);

  // Días con tareas (para marcar en calendario)
  const daysWithTasks = useMemo(() => {
    const days = new Map<string, { count: number; hasOverdue: boolean }>();
    filteredTasks.forEach(task => {
      const dateKey = format(new Date(task.scheduled_date), 'yyyy-MM-dd');
      const current = days.get(dateKey) || { count: 0, hasOverdue: false };
      days.set(dateKey, {
        count: current.count + 1,
        hasOverdue: current.hasOverdue || task.status === 'overdue',
      });
    });
    return days;
  }, [filteredTasks]);

  // Obtener nombre de máquina
  const getMachineName = (machineId: string) => {
    const machine = plotters.find(p => p.id === machineId);
    return machine?.name || 'Plotter';
  };

  // Handlers
  const handleNewTask = () => {
    setEditingTask(null);
    setFormOpen(true);
  };

  const handleEditTask = (task: PlotterMaintenance) => {
    setEditingTask(task);
    setFormOpen(true);
  };

  const handleDeleteTask = async (task: PlotterMaintenance) => {
    if (!confirm(`¿Eliminar la tarea "${task.title}"?`)) return;
    await deleteMutation.mutateAsync(task.id);
  };

  const handleStartTask = async (task: PlotterMaintenance) => {
    await changeStatusMutation.mutateAsync({ id: task.id, status: 'in_progress' });
  };

  const handleCompleteTask = (task: PlotterMaintenance) => {
    setCompletingTask(task);
    setCompleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <MaintenanceStats machineId={selectedMachineId === 'all' ? undefined : selectedMachineId} />

      {/* Controles */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Búsqueda */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar tareas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filtro de plotter */}
        <Select value={selectedMachineId} onValueChange={setSelectedMachineId}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Todos los plotters" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los plotters</SelectItem>
            {plotters.map((plotter) => (
              <SelectItem key={plotter.id} value={plotter.id}>
                {plotter.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtro de estado */}
        <Select value={selectedStatus} onValueChange={(v) => setSelectedStatus(v as any)}>
          <SelectTrigger className="w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Toggle vista */}
        <div className="flex border rounded-lg overflow-hidden">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            className="rounded-none"
          >
            <CalendarDays className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="rounded-none"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>

        {/* Nuevo */}
        {canManage && (
          <Button onClick={handleNewTask} className="bg-gradient-magma">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Tarea
          </Button>
        )}
      </div>

      {/* Vista */}
      {viewMode === 'calendar' ? (
        <div className="grid lg:grid-cols-[350px_1fr] gap-6">
          {/* Calendario */}
          <Card className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={selectedMonth}
              onMonthChange={setSelectedMonth}
              locale={es}
              className="rounded-md"
              modifiers={{
                hasTasks: (date) => daysWithTasks.has(format(date, 'yyyy-MM-dd')),
                hasOverdue: (date) => {
                  const data = daysWithTasks.get(format(date, 'yyyy-MM-dd'));
                  return data?.hasOverdue || false;
                },
              }}
              modifiersStyles={{
                hasTasks: {
                  fontWeight: 'bold',
                  backgroundColor: 'hsl(var(--primary) / 0.1)',
                },
                hasOverdue: {
                  color: 'hsl(var(--destructive))',
                  fontWeight: 'bold',
                },
              }}
            />
          </Card>

          {/* Tareas del día */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">
              {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
            </h3>

            {tasksForSelectedDate.length === 0 ? (
              <Card className="p-8 text-center">
                <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  No hay tareas programadas para este día
                </p>
                {canManage && (
                  <Button variant="outline" className="mt-4" onClick={handleNewTask}>
                    Programar tarea
                  </Button>
                )}
              </Card>
            ) : (
              <div className="space-y-3">
                {tasksForSelectedDate.map((task) => (
                  <MaintenanceCard
                    key={task.id}
                    task={task}
                    machineName={getMachineName(task.machine_id)}
                    onEdit={handleEditTask}
                    onDelete={handleDeleteTask}
                    onComplete={handleCompleteTask}
                    onStart={handleStartTask}
                    canManage={canManage}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Vista lista */
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <Card className="p-12 text-center">
              <CalendarDays className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg text-muted-foreground">
                No hay tareas de mantenimiento
              </p>
              {canManage && (
                <Button variant="outline" className="mt-4" onClick={handleNewTask}>
                  Programar primera tarea
                </Button>
              )}
            </Card>
          ) : (
            filteredTasks.map((task) => (
              <MaintenanceCard
                key={task.id}
                task={task}
                machineName={getMachineName(task.machine_id)}
                onEdit={handleEditTask}
                onDelete={handleDeleteTask}
                onComplete={handleCompleteTask}
                onStart={handleStartTask}
                canManage={canManage}
              />
            ))
          )}
        </div>
      )}

      {/* Dialogs */}
      <MaintenanceFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editingTask={editingTask}
        defaultMachineId={selectedMachineId !== 'all' ? selectedMachineId : undefined}
      />

      <CompleteMaintenanceDialog
        open={completeDialogOpen}
        onOpenChange={setCompleteDialogOpen}
        task={completingTask}
      />
    </div>
  );
}
